import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  created_at: string;
  is_demo: boolean;
  total_users: number;
  mau: number;
  wau: number;
  pilot_mode: boolean;
}

export interface ActivityEntry {
  activity_id: string;
  action_type: string;
  page_path: string;
  created_at: string;
  user_email: string;
  tenant_slug: string;
  tenant_name: string;
}

export interface SuperadminUser {
  user_id: string;
  email: string;
  created_at: string;
  tenant_id: string | null;
  tenant_slug: string;
  tenant_name: string;
  role: string;
  last_active: string | null;
}

export interface PlanDistribution {
  plan: string;
  count: number;
}

export interface TenantGrowth {
  month: string;
  new_tenants: number;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  created_at: string;
  actor_email: string | null;
  target_tenant_name: string | null;
  target_tenant_slug: string | null;
  target_user_email: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export function useSuperadminCheck() {
  const { user, loading } = useAuth();
  const [isSuperadmin, setIsSuperadmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (loading) return;                         // wait for auth to settle
    if (!user) { setIsSuperadmin(false); return; }
    supabase.rpc('check_superadmin').then(({ data }) => {
      setIsSuperadmin(data ?? false);
    });
  }, [user, loading]);

  return isSuperadmin;
}

export function useSuperadminDashboard() {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [planDist, setPlanDist] = useState<PlanDistribution[]>([]);
  const [growth, setGrowth] = useState<TenantGrowth[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [t, a, p, g] = await Promise.all([
      supabase.rpc('get_sa_tenant_summary'),
      supabase.rpc('get_sa_activity_feed', { p_limit: 20 }),
      supabase.rpc('get_sa_plan_distribution'),
      supabase.rpc('get_sa_tenant_growth'),
    ]);
    setTenants((t.data as TenantSummary[]) ?? []);
    setActivity((a.data as ActivityEntry[]) ?? []);
    setPlanDist((p.data as PlanDistribution[]) ?? []);
    setGrowth((g.data as TenantGrowth[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const totalUsers = tenants.reduce((s, t) => s + Number(t.total_users), 0);
  const totalMAU   = tenants.reduce((s, t) => s + Number(t.mau), 0);

  return { tenants, activity, planDist, growth, loading, totalUsers, totalMAU, refetch: load };
}

export function useSuperadminUsers() {
  const [users, setUsers] = useState<SuperadminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    supabase.rpc('get_sa_all_users').then(({ data }) => {
      setUsers((data as SuperadminUser[]) ?? []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  return { users, loading, refetch: load };
}

export async function setTenantStatus(tenantId: string, status: 'active' | 'suspended') {
  const { error } = await supabase.rpc('sa_set_tenant_status', { p_tenant_id: tenantId, p_status: status });
  if (error) throw error;
}

export async function setTenantPlan(tenantId: string, plan: string) {
  const { error } = await supabase.rpc('sa_set_tenant_plan', { p_tenant_id: tenantId, p_plan: plan });
  if (error) throw error;
}

export async function setTenantPilotMode(tenantId: string, enabled: boolean) {
  const { error } = await supabase.rpc('sa_set_tenant_pilot_mode', { p_tenant_id: tenantId, p_enabled: enabled });
  if (error) throw error;
}

export async function setTenantDemoMode(tenantId: string, isDemo: boolean) {
  const { error } = await supabase.rpc('sa_set_tenant_demo_mode', { p_tenant_id: tenantId, p_is_demo: isDemo });
  if (error) throw error;
}

export interface DemoDataCounts {
  projects_sync: number;
  tasks_sync: number;
  kpis_sync: number;
  meetings_sync: number;
  emails_sync: number;
  risk_signals: number;
  documents: number;
  decisions: number;
  user_activities: number;
}

export interface DemoDataResult {
  counts: DemoDataCounts;
  success?: boolean;
  // Whether the app still falls back to built-in mock data for this demo
  // tenant when a view has no seeded rows. Flush sets this false; Populate
  // and Re-seed set it true.
  mockDataEnabled?: boolean;
  errors?: Array<{ table: string; message: string }>;
}

async function invokeDemoData(action: "status" | "populate" | "flush", tenantId: string): Promise<DemoDataResult> {
  const { data, error } = await supabase.functions.invoke("superadmin-demo-data", {
    body: { action, tenantId },
  });
  if (error) throw new Error(error.message);
  return data as DemoDataResult;
}

export function getDemoDataStatus(tenantId: string) {
  return invokeDemoData("status", tenantId);
}

export function populateDemoData(tenantId: string) {
  return invokeDemoData("populate", tenantId);
}

export function flushDemoData(tenantId: string) {
  return invokeDemoData("flush", tenantId);
}

export async function setUserRole(userId: string, tenantId: string, role: string) {
  const { error } = await supabase.rpc('sa_set_user_role', { p_user_id: userId, p_tenant_id: tenantId, p_role: role });
  if (error) throw error;
}

export async function removeUserFromTenant(userId: string, tenantId: string) {
  const { error } = await supabase.rpc('sa_remove_user_from_tenant', { p_user_id: userId, p_tenant_id: tenantId });
  if (error) throw error;
}

export function useSuperadminAuditLog() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.rpc('get_sa_audit_log', { p_limit: 20 }).then(({ data }) => {
      setEntries((data as AuditLogEntry[]) ?? []);
      setLoading(false);
    });
  }, []);

  return { entries, loading };
}
